import { z } from "zod";
import { SLUG_PATTERN } from "./sw-contract.ts";
import { checkAccent, contrastRatio } from "./color.ts";

/**
 * business.json — the only per-studio input of the pipeline (plus images).
 * Validated here before anything is published; the database re-checks the
 * invariants it depends on.
 */

const key = z.string().regex(/^[a-z0-9][a-z0-9-]{0,47}$/, "lowercase latin, digits and dashes");
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$|^24:00$/, "HH:MM");
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD");
const imageFile = z.string().regex(/^[A-Za-z0-9._-]+\.(jpe?g|png|webp)$/i, "file name in images/");
const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/, "#RRGGBB");

function isTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

const minutes = (s: string) => {
  const [h, m] = s.split(":").map(Number);
  return h! * 60 + m!;
};

export const businessSchema = z
  .object({
    $schema: z.string().optional(),
    slug: z.string().regex(SLUG_PATTERN, "3–40 chars: a-z, 0-9, dashes, not at the ends"),
    /** Demo studios contain only demo data and can never go live. */
    demo: z.boolean().default(false),
    /** Open questions for the owner. While non-empty the studio cannot go live. */
    review_notes: z.array(z.string().max(300)).default([]),
    name: z.string().min(1).max(80),
    short_name: z.string().min(1).max(12),
    tagline: z.string().max(120).default(""),
    description: z.string().max(600).default(""),
    category: z.string().max(40).default(""),
    timezone: z.string().refine(isTimeZone, "unknown IANA time zone"),
    currency: z.string().regex(/^[A-Z]{3}$/),
    locale: z.string().default("ru-RU"),
    /** Interface languages offered to visitors; one language hides the switch. */
    languages: z.array(z.enum(["ru", "kk"])).min(1).default(["ru", "kk"]),
    /** Heading typeface to match a brand: soft serif (default), casual handwritten script, flourished calligraphy, or a refined high-contrast serif ("elegant"). */
    heading_font: z.enum(["serif", "script", "calligraphy", "elegant"]).default("serif"),
    accent: hex,
    /** Text on accent-filled buttons (e.g. gold on black); default: black or white, whichever reads better. */
    accent_text: hex.optional(),
    /** Decorative brand colour for rings, glass tint, frames (e.g. gold); default: the accent. */
    detail: hex.optional(),
    resource_label: z.object({ one: z.string().max(30), many: z.string().max(30) }).default({ one: "Мастер", many: "Мастера" }),
    contacts: z.object({
      phone: z.string().max(30).default(""),
      address: z.string().max(200).default(""),
      map_url: z.url().optional(),
      /** [latitude, longitude] of the entrance: pins the embedded map and the route. */
      geo: z.tuple([z.number().min(-90).max(90), z.number().min(-180).max(180)]).optional(),
      instagram: z.url().optional(),
      vk: z.url().optional(),
      telegram: z.url().optional(),
      whatsapp: z.url().optional(),
      website: z.url().optional(),
    }),
    policies: z
      .object({
        cancellation: z.string().max(400).default(""),
        prepayment: z.string().max(400).default(""),
      })
      .default({ cancellation: "", prepayment: "" }),
    booking: z
      .object({
        slot_step_minutes: z.number().int().min(5).max(240).default(30),
        lead_time_minutes: z.number().int().min(0).max(10080).default(60),
        horizon_days: z.number().int().min(1).max(365).default(30),
        cancel_cutoff_minutes: z.number().int().min(0).max(10080).default(120),
      })
      .default({ slot_step_minutes: 30, lead_time_minutes: 60, horizon_days: 30, cancel_cutoff_minutes: 120 }),
    assistant: z
      .object({ enabled: z.boolean().default(false), daily_token_budget: z.number().int().min(0).max(5_000_000).default(50000) })
      .default({ enabled: false, daily_token_budget: 50000 }),
    resources: z
      .array(z.object({ key, name: z.string().min(1).max(80), kind: z.string().min(1).max(32).default("staff") }))
      .min(1),
    services: z
      .array(
        z.object({
          key,
          name: z.string().min(1).max(120),
          description: z.string().max(2000).default(""),
          category: z.string().max(60).default(""),
          duration_minutes: z.number().int().min(5).max(20160),
          buffer_after_minutes: z.number().int().min(0).max(1440).default(0),
          multi_day: z.boolean().default(false),
          price: z.number().min(0).max(10_000_000),
          price_is_from: z.boolean().default(false),
          /** Price not published yet (shown as "по запросу"); requires price 0. */
          price_on_request: z.boolean().default(false),
          image: imageFile.optional(),
          resources: z.array(key).min(1),
        }),
      )
      .min(1),
    hours: z
      .array(z.object({ days: z.array(z.number().int().min(1).max(7)).min(1), opens: hhmm, closes: hhmm }))
      .min(1),
    exceptions: z
      .array(
        z.object({
          date: isoDate,
          closed: z.boolean().default(true),
          opens: hhmm.optional(),
          closes: hhmm.optional(),
          note: z.string().max(100).default(""),
        }),
      )
      .default([]),
    images: z.object({
      /** Square source for every icon (≥ 512×512). */
      icon: imageFile,
      cover: imageFile,
      gallery: z.array(z.object({ file: imageFile, alt: z.string().max(200).default("") })).max(24).default([]),
    }),
  })
  .superRefine((b, ctx) => {
    const accent = checkAccent(b.accent);
    for (const p of accent.problems) ctx.addIssue({ code: "custom", path: ["accent"], message: p });
    if (b.accent_text && contrastRatio(b.accent, b.accent_text) < 4.5) {
      ctx.addIssue({ code: "custom", path: ["accent_text"], message: `accent_text on accent contrast is ${contrastRatio(b.accent, b.accent_text).toFixed(2)}:1, needs ≥ 4.5:1` });
    }

    const dup = (arr: string[]) => arr.filter((x, i) => arr.indexOf(x) !== i);
    for (const k of dup(b.resources.map((r) => r.key))) ctx.addIssue({ code: "custom", path: ["resources"], message: `duplicate resource key "${k}"` });
    for (const k of dup(b.services.map((s) => s.key))) ctx.addIssue({ code: "custom", path: ["services"], message: `duplicate service key "${k}"` });
    // Media paths are derived from file content: one file on two services would collide on publish.
    for (const f of dup(b.services.flatMap((s) => (s.image ? [s.image] : []))))
      ctx.addIssue({ code: "custom", path: ["services"], message: `image "${f}" is used by more than one service; give each service its own file` });

    const resourceKeys = new Set(b.resources.map((r) => r.key));
    b.services.forEach((s, i) => {
      for (const r of s.resources) {
        if (!resourceKeys.has(r)) ctx.addIssue({ code: "custom", path: ["services", i, "resources"], message: `unknown resource "${r}"` });
      }
    });

    b.hours.forEach((h, i) => {
      if (minutes(h.closes) <= minutes(h.opens)) ctx.addIssue({ code: "custom", path: ["hours", i], message: "closes must be after opens" });
    });
    // Overlapping windows on the same weekday would produce duplicate moments.
    for (let d = 1; d <= 7; d++) {
      const win = b.hours.filter((h) => h.days.includes(d)).map((h) => [minutes(h.opens), minutes(h.closes)] as const).sort((x, y) => x[0] - y[0]);
      for (let i = 1; i < win.length; i++) {
        if (win[i]![0] < win[i - 1]![1]) ctx.addIssue({ code: "custom", path: ["hours"], message: `overlapping hours on weekday ${d}` });
      }
    }

    b.services.forEach((s, i) => {
      if (s.multi_day) return;
      const longest = Math.max(...b.hours.map((h) => minutes(h.closes) - minutes(h.opens)));
      if (s.duration_minutes > longest) {
        ctx.addIssue({
          code: "custom",
          path: ["services", i, "duration_minutes"],
          message: `longer than any working window (${longest} min); set "multi_day": true if the work runs overnight`,
        });
      }
    });

    b.exceptions.forEach((e, i) => {
      if (!e.closed && (!e.opens || !e.closes || minutes(e.closes) <= minutes(e.opens))) {
        ctx.addIssue({ code: "custom", path: ["exceptions", i], message: "open exception needs opens < closes" });
      }
    });

    b.services.forEach((s, i) => {
      if (s.price_on_request && s.price !== 0) ctx.addIssue({ code: "custom", path: ["services", i, "price"], message: "price must be 0 when price_on_request is true" });
      if (!s.price_on_request && s.price === 0) ctx.addIssue({ code: "custom", path: ["services", i, "price"], message: "price 0 needs price_on_request: true" });
    });

    if (!b.demo && (!b.contacts.phone || !b.contacts.address)) {
      ctx.addIssue({ code: "custom", path: ["contacts"], message: "phone and address are required for a real studio" });
    }
  });

export type BusinessInput = z.input<typeof businessSchema>;
export type Business = z.output<typeof businessSchema>;

/** Public, non-personal presentation stored in tenants.public_config. */
export const publicConfigSchema = z.object({
  is_demo: z.boolean(),
  review_notes: z.array(z.string()),
  short_name: z.string(),
  tagline: z.string(),
  description: z.string(),
  category: z.string(),
  accent: hex,
  accent_text: hex.optional(),
  detail: hex.optional(),
  /** Absent in configs published before languages existed: both are offered. */
  languages: z.array(z.enum(["ru", "kk"])).min(1).optional(),
  heading_font: z.enum(["serif", "script", "calligraphy", "elegant"]).optional(),
  resource_label: z.object({ one: z.string(), many: z.string() }),
  contacts: z.object({
    phone: z.string(),
    address: z.string(),
    map_url: z.string().optional(),
    geo: z.tuple([z.number(), z.number()]).optional(),
    instagram: z.string().optional(),
    vk: z.string().optional(),
    telegram: z.string().optional(),
    whatsapp: z.string().optional(),
    website: z.string().optional(),
  }),
  policies: z.object({ cancellation: z.string(), prepayment: z.string() }),
});
export type PublicConfig = z.infer<typeof publicConfigSchema>;

export interface MediaUpload {
  kind: "cover" | "gallery" | "service";
  /** Path inside the studio-media bucket. */
  storage_path: string;
  alt: string;
  service_key?: string;
  sort: number;
}

/** Map a validated business.json to the admin_publish_tenant payload. */
export function toPublishPayload(b: Business, media: MediaUpload[]) {
  const publicConfig: PublicConfig = {
    is_demo: b.demo,
    review_notes: b.review_notes,
    short_name: b.short_name,
    tagline: b.tagline,
    description: b.description,
    category: b.category,
    accent: b.accent.toUpperCase(),
    accent_text: b.accent_text?.toUpperCase(),
    detail: b.detail?.toUpperCase(),
    languages: b.languages,
    heading_font: b.heading_font,
    resource_label: b.resource_label,
    contacts: b.contacts,
    policies: b.policies,
  };
  const imageByService = new Map(media.filter((m) => m.kind === "service").map((m) => [m.service_key, m.storage_path]));
  return {
    slug: b.slug,
    name: b.name,
    timezone: b.timezone,
    currency: b.currency,
    locale: b.locale,
    public_config: publicConfig,
    rules: b.booking,
    ai: { enabled: b.assistant.enabled, daily_token_budget: b.assistant.daily_token_budget },
    resources: b.resources.map((r, i) => ({ key: r.key, name: r.name, kind: r.kind, sort: i })),
    services: b.services.map((s, i) => ({
      key: s.key,
      name: s.name,
      description: s.description,
      category: s.category,
      duration_minutes: s.duration_minutes,
      buffer_after_minutes: s.buffer_after_minutes,
      fits_working_day: !s.multi_day,
      price_amount: s.price,
      price_is_from: s.price_is_from,
      price_on_request: s.price_on_request,
      image_path: imageByService.get(s.key) ?? null,
      sort: i,
      resource_keys: s.resources,
    })),
    hours: b.hours.flatMap((h) => h.days.map((weekday) => ({ weekday, opens: h.opens, closes: h.closes }))),
    exceptions: b.exceptions.map((e) => ({
      day: e.date,
      is_closed: e.closed,
      opens: e.closed ? null : e.opens,
      closes: e.closed ? null : e.closes,
      note: e.note,
    })),
    media: media.map((m) => ({ kind: m.kind, storage_path: m.storage_path, alt: m.alt, service_key: m.service_key, sort: m.sort })),
  };
}
